/**
 * The Tickets tab of a Well Servicing job (fieldiq-api#1294, WS-4). JobSections.tsx
 * is not edited; these sit beside it, and JobDetailScreen.tsx only gains the tab.
 *
 *   WsJobTicketsSection  the job's tickets, and "Create ticket" — per job, or
 *                        per day with a date when the job is ticketed per day
 *                        (the job's ticketMode, which the API also decides by).
 *                        Drafting reads the OFFICE-APPROVED tour sheets; a
 *                        refusal (no sheet, not approved, already ticketed, a
 *                        ticket already, the other scope) is a sentence.
 *   WsRentInBillingPanel the billing rate of each rent-in, shown on the job's
 *                        Rent-ins tab: rented-in equipment or other rates only,
 *                        from this job's customer card or the default card
 *                        (`rate_not_rent_in`, `rate_other_customer`). A billable
 *                        rent-in bills on the days it is on rent on a tour sheet;
 *                        with no billing rate it drafts onto a ticket unpriced.
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateWsTicket, useWsRates, useWsRentInBillingRates, useWsTickets, useSetWsRentInBillingRate } from '../../../hooks/useWsTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wsScopeText, wsTicketRefusal, wsTicketStatusSkin } from '../../../lib/ws-tickets';
import type { WsJob, WsRentIn } from '../../../types/well-servicing';
import type { WsTicket } from '../../../types/ws-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WS_DATE_INPUT, WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsBadge, WsEmpty, WsField, WsFormError, WsPanel, WsRegister, type WsColumn } from '../WsBits';
import { wsTicketTotalText } from './TicketsScreen';
import { wsRateOption, WsUnpricedFlag } from './TicketBits';

/** The kinds a rent-in may bill at (mirrors the API's WS_RENT_IN_RATE_KINDS, lib/ws-rates.ts). */
export const WS_RENT_IN_RATE_KINDS: string[] = ['rent_in', 'other'];

const columns: WsColumn<WsTicket>[] = [
  { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
  { key: 'scope', header: 'Covers', render: (t) => wsScopeText(t) },
  { key: 'lines', header: 'Lines', align: 'right', render: (t) => String(t.lineCount ?? '—') },
  { key: 'status', header: 'Status', render: (t) => <WsBadge skin={wsTicketStatusSkin(t.status)} value={t.status} /> },
  {
    key: 'total',
    header: 'Total',
    align: 'right',
    render: (t) => (
      <span className="whitespace-nowrap">
        <span className="font-mono">{wsTicketTotalText(t)}</span>
        <WsUnpricedFlag count={t.unpricedCount} />
      </span>
    ),
  },
];

export function WsJobTicketsSection({ job, canWrite, today }: { job: WsJob; canWrite: boolean; today: string | null }) {
  const openRecord = useOpenRecord();
  const query = useWsTickets({ job_id: job.id });
  const create = useCreateWsTicket();
  const perDay = job.ticketMode === 'per_day';
  const [creating, setCreating] = useState(false);
  const [day, setDay] = useState(today ?? '');
  const [error, setError] = useState<string | null>(null);
  const tickets = query.data?.data ?? [];

  const draft = () => {
    setError(null);
    if (perDay && !/^\d{4}-\d{2}-\d{2}$/.test(day)) return setError('This job is ticketed per day. Pick the day to ticket.');
    create.mutate(
      { jobId: job.id, workDate: perDay ? day : undefined },
      {
        onSuccess: (r) => {
          toast.success(r.data ? `${r.data.ticketNumber} drafted from the tour sheets` : 'Ticket drafted');
          setCreating(false);
          if (r.data) openRecord(`/well-servicing/tickets/${r.data.id}`);
        },
        onError: (e) => setError(wsTicketRefusal(e, 'The ticket could not be drafted.')),
      },
    );
  };

  return (
    <WsPanel
      eyebrow="Field tickets"
      title={perDay ? 'Ticketed per day' : 'Ticketed per job'}
      testId="ws-job-tickets"
      actions={
        canWrite && !creating ? (
          <button type="button" className={WS_PRIMARY} onClick={() => { setError(null); setCreating(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Create ticket
          </button>
        ) : undefined
      }
    >
      {creating && (
        <div data-testid="ws-job-ticket-create" className="space-y-3 border-b border-fieldiq-border px-4 py-4">
          <p className="text-sm text-fieldiq-text">
            {perDay
              ? 'Drafts one ticket for the day you pick, from that day’s office-approved tour sheet. Each line is priced from the rate card where a rate fits; a line with no rate is drafted unpriced.'
              : 'Drafts one ticket covering every office-approved tour sheet not already on a ticket. Each line is priced from the rate card where a rate fits; a line with no rate is drafted unpriced.'}
          </p>
          <WsFormError message={error} />
          {perDay && (
            <div className="grid gap-3 sm:grid-cols-[14rem]">
              <WsField label="Day to ticket *">
                <input className={WS_DATE_INPUT} type="date" aria-label="Day to ticket" value={day} onChange={(e) => setDay(e.target.value)} />
              </WsField>
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={WS_SECONDARY} onClick={() => setCreating(false)} disabled={create.isPending}>
              Cancel
            </button>
            <button type="button" className={WS_PRIMARY} onClick={draft} disabled={create.isPending}>
              {create.isPending ? 'Drafting…' : 'Draft ticket'}
            </button>
          </div>
        </div>
      )}
      <WsRegister
        query={query}
        rows={tickets}
        columns={columns}
        rail={(t) => wsTicketStatusSkin(t.status).rail}
        onOpen={(t) => openRecord(`/well-servicing/tickets/${t.id}`)}
        subject="field tickets"
        testId="ws-job-ticket"
        minWidth="min-w-[640px]"
        empty={<WsEmpty>No tickets for this job yet.</WsEmpty>}
        card={(t) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {wsScopeText(t)} · Total {wsTicketTotalText(t)}
                <WsUnpricedFlag count={t.unpricedCount} testId="ws-unpriced-flag-card" />
              </p>
            </div>
            <WsBadge skin={wsTicketStatusSkin(t.status)} value={t.status} />
          </div>
        )}
      />
    </WsPanel>
  );
}

function RentInRateRow({
  rentIn,
  current,
  loading,
  failed,
  options,
  canWrite,
}: {
  rentIn: WsRentIn;
  current: { id: string; label: string } | null;
  loading: boolean;
  failed: boolean;
  options: { id: string; label: string }[];
  canWrite: boolean;
}) {
  const set = useSetWsRentInBillingRate();
  const [error, setError] = useState<string | null>(null);
  return (
    <li data-testid="ws-rent-in-billing-row" className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-white">{rentIn.description || '—'}</p>
          <p className="text-xs text-fieldiq-muted">
            {rentIn.vendorName || '—'}
            {rentIn.unitNumber ? ` · ${rentIn.unitNumber}` : ''}
          </p>
        </div>
        <div className="w-full sm:w-[28rem]">
          {canWrite ? (
            <select
              className={WS_INPUT}
              aria-label={`Billing rate for ${rentIn.description}`}
              value={current?.id ?? ''}
              disabled={loading || failed || set.isPending}
              onChange={(e) => {
                setError(null);
                set.mutate(
                  { rentInId: rentIn.id, rateId: e.target.value || null },
                  {
                    onSuccess: () => toast.success(e.target.value ? 'Billing rate set' : 'Billing rate cleared'),
                    onError: (err) => setError(wsTicketRefusal(err, 'The billing rate could not be set.')),
                  },
                );
              }}
            >
              <option value="">{loading ? 'Loading…' : 'Not billed from the rate card'}</option>
              {current && !options.some((o) => o.id === current.id) && <option value={current.id}>{current.label}</option>}
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-fieldiq-text">{loading ? '…' : current?.label ?? '—'}</p>
          )}
        </div>
      </div>
      {failed && <WsFormError message="This rent-in's billing rate could not be read." />}
      <WsFormError message={error} />
    </li>
  );
}

export function WsRentInBillingPanel({ job, rentIns, canWrite }: { job: WsJob; rentIns: WsRentIn[]; canWrite: boolean }) {
  const ratesQuery = useWsRates(undefined, canWrite);
  const billing = useWsRentInBillingRates(rentIns.map((r) => r.id));
  const options = (ratesQuery.data?.data ?? [])
    .filter((r) => WS_RENT_IN_RATE_KINDS.includes(r.kind) && (r.customerId === null || r.customerId === job.customerId))
    .map((r) => ({ id: r.id, label: wsRateOption(r) }));
  return (
    <WsPanel eyebrow="Billing" title="Rent-in billing rates" testId="ws-rent-in-billing">
      <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-muted">
        What the customer is billed for each rent-in, from the rate card — a rented-in equipment or other rate on this customer’s card or the default card. A
        billable rent-in bills on each tour sheet it is on rent for, and one with no billing rate drafts onto a ticket unpriced.
      </p>
      {canWrite && queryLoadFailure(ratesQuery) && <div className="px-4 pt-3"><WsFormError message="The rate card could not be read, so a billing rate cannot be picked right now." /></div>}
      {!rentIns.length ? (
        <WsEmpty>No rent-ins on this job.</WsEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rentIns.map((ri, i) => {
            const q = billing[i];
            const rate = q?.data?.data?.rate ?? null;
            return (
              <RentInRateRow
                key={ri.id}
                rentIn={ri}
                current={rate ? { id: rate.id, label: wsRateOption(rate) } : null}
                loading={!!q?.isLoading}
                failed={!!q && !!queryLoadFailure(q)}
                options={options}
                canWrite={canWrite}
              />
            );
          })}
        </ul>
      )}
    </WsPanel>
  );
}

